import type { Bbox } from '@/types/domain';

/**
 * Loads the il outline used to draw a province's border on a map. Shared by
 * the create-listing picker and the Explore map so the two cannot drift apart
 * in behaviour.
 */

/** Fitting an extent beats a guessed zoom: one constant cannot frame both Konya and Yalova. */
export const TURKEY_BBOX: Bbox = [25.6, 35.8, 44.8, 42.2];

/** Border filenames are the province name folded to bare ASCII (see scripts/buildLocationGeo.mjs). */
export function foldName(name: string): string {
  return name
    .normalize('NFD')
    .replace(/\p{Mn}/gu, '')
    .replace(/[ıİ]/g, 'i')
    .replace(/ğ/gi, 'g')
    .replace(/ş/gi, 's')
    .replace(/ç/gi, 'c')
    .replace(/ö/gi, 'o')
    .replace(/ü/gi, 'u')
    .toLowerCase()
    .replace(/[^a-z]/g, '');
}

// GeoJSON geometry of one province outline, keyed by folded name. Borders never
// change, so a plain module-level cache is enough — one fetch per il per session.
const borders = new Map<string, Promise<object | undefined>>();

/**
 * Loads an il outline from /borders/<folded>.json. Resolves to undefined on any
 * failure: the border is orientation only, and a map without it still works.
 */
export function loadBorder(city?: string): Promise<object | undefined> {
  if (!city) return Promise.resolve(undefined);
  const key = foldName(city);
  let pending = borders.get(key);
  if (!pending) {
    pending = fetch(`/borders/${key}.json`)
      .then((res) => (res.ok ? res.json() : undefined))
      .catch(() => undefined);
    borders.set(key, pending);
  }
  return pending;
}
